import { cn } from '@/lib/utils';
import { FacebookComment } from '@/types/fb-type';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LiveCommentsFab } from './live-comments-fab';
import { LiveCommentsList, LiveCommentsPanelFooter } from './live-comments-list';
import { LiveCommentsPanelHeader } from './live-comments-panel-header';
import {
  isCompactPanelSize,
  isDensePanelSize,
  LiveCommentsSizePreset,
} from './live-comments-panel-layout';
import { useLiveCommentsFloatingWidget } from './use-live-comments-floating-widget';

export type LiveCommentsFloatingWidgetProps = {
  comments: FacebookComment[];
  isLoading?: boolean;
  refreshDuration?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialSize?: LiveCommentsSizePreset;
  title?: string;
  onPinToggle?: (id: string, pinned: boolean) => void;
  onRefresh?: () => void;
  loadMore?: boolean;
};

export function LiveCommentsFloatingWidget({
  comments,
  isLoading = false,
  open: openProp,
  onOpenChange,
  initialSize = 'sm',
  title = 'Live Comments',
  loadMore = false,
  onRefresh,
  onPinToggle,
}: LiveCommentsFloatingWidgetProps) {
  const [mounted, setMounted] = useState(false);
  const isControlled = typeof openProp === 'boolean';

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isMobile,
    isOpen,
    size,
    setSize,
    toggleSize,
    unread,
    searchQuery,
    setSearchQuery,
    showNewBanner,
    filteredComments,
    panelClassName,
    handleOpenChange,
  } = useLiveCommentsFloatingWidget({
    comments,
    open: openProp ?? false,
    initialSize,
    isControlled,
    onOpenChange,
  });

  if (!mounted) return null;

  return createPortal(
    <>
      {!isOpen && (
        <LiveCommentsFab
          isLoading={isLoading}
          unread={unread}
          onOpen={() => handleOpenChange(true)}
        />
      )}

      {isOpen && (
        <div
          role="dialog"
          aria-label="Live comments window"
          className={cn(
            'fixed z-[1400] flex flex-col overflow-hidden border border-gray-200 bg-white shadow-2xl',
            'backdrop-blur supports-[backdrop-filter]:bg-white/95',
            isDensePanelSize(size) ? 'rounded-lg' : 'rounded-xl',
            panelClassName,
          )}
        >
          <LiveCommentsPanelHeader
            title={title}
            commentsCount={comments.length}
            isMobile={isMobile}
            size={size}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSizeChange={setSize}
            onToggleSize={toggleSize}
            onMinimize={() => handleOpenChange(false)}
            onClose={() => handleOpenChange(false)}
            onRefresh={onRefresh}
            isRefreshing={loadMore}
          />

          <LiveCommentsList
            comments={comments}
            filteredComments={filteredComments}
            isLoading={isLoading}
            showNewBanner={showNewBanner}
            onPinToggle={onPinToggle}
            compact={isCompactPanelSize(size)}
            dense={isDensePanelSize(size)}
          />

          <LiveCommentsPanelFooter
            isLoading={isLoading}
            hasComments={comments.length > 0}
            dense={isDensePanelSize(size)}
          />
        </div>
      )}
    </>,
    document.body,
  );
}

export default LiveCommentsFloatingWidget;
